/**
 * userAudit.logic.ts
 *
 * The decisions behind the User Audit page, kept free of React and of the app
 * entry so they can be tested on their own: whether the chosen dates may be
 * asked for at all, what is sent for the list and for the download, which
 * notice the page shows, and how times and staff names are written.
 *
 * The server (UserAuditQuery::fromInput) enforces the same date rules and says
 * so in its own words; checking here first is what stops the page asking for a
 * list it already knows will be refused, and keeps Export off with it.
 */
import axios from 'axios';
import type { AdminUser } from '@/services/admin.service';
import type { FilterOption } from '@/types/filters.types';

/** Mirrors UserAuditQuery::MAX_RANGE_DAYS — Carbon's diffInDays, inclusive of 366. */
export const MAX_RANGE_DAYS = 366;

export const PER_PAGE = 50;

/** R24 — always on screen, not only when the list is empty. */
export const NEVER_RECORDED_LINE =
  'Not shown here: actions that changed nothing, activity by anyone not signed in as staff, and the rare action that timed out before finishing.';

export const RANGE_REQUIRED_MESSAGE = 'Pick a date range of up to one year to see activity.';

export const RANGE_REVERSED_MESSAGE =
  'The end date is before the start date. Pick an end date on or after the start date.';

export const LOAD_FAILED_MESSAGE =
  'Activity could not be loaded. Try again, and contact support if it keeps happening.';

export const NOTHING_RECORDED_YET = 'No activity has been recorded for this account yet.';

/** The server's refusal code (GatesUserAudit). Its message is already plain. */
export const DENIED_CODE = 'USER_AUDIT_DENIED';

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

function utcDay(ymd: string): number | null {
  const m = YMD.exec(ymd);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(t) ? null : t;
}

export type RangeCheck = { ok: true } | { ok: false; message: string };

/**
 * Whether a list may be asked for over these dates. Both ends are required and
 * the span is capped, exactly as the server caps it.
 */
export function checkDateRange(from: string | undefined, to: string | undefined): RangeCheck {
  const start = from ? utcDay(from) : null;
  const end = to ? utcDay(to) : null;
  if (start === null || end === null) return { ok: false, message: RANGE_REQUIRED_MESSAGE };
  if (end < start) return { ok: false, message: RANGE_REVERSED_MESSAGE };
  if (Math.round((end - start) / 86_400_000) > MAX_RANGE_DAYS) {
    return { ok: false, message: RANGE_REQUIRED_MESSAGE };
  }
  return { ok: true };
}

/** The search box takes a number shown on screen; anything else is dropped. */
export function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

/**
 * What the Staff filter sends: every ticked id, comma-separated, or '' for all
 * staff. Ticking every Staff option (Select All) also sends '', so the
 * server's cap on how many staff one choice may name
 * (UserAuditQuery::MAX_STAFF_FILTER) is only reached by a hand-picked list.
 * While the options are still loading there is nothing to compare against,
 * and the ticked ids are sent as they are.
 */
export function staffFilterParam(selected: string[] | undefined, options: FilterOption[]): string {
  const ticked = selected ?? [];
  if (ticked.length === 0) return '';
  if (options.length > 0) {
    const chosen = new Set(ticked);
    if (options.every((o) => chosen.has(o.value))) return '';
  }
  return ticked.join(',');
}

export interface UserAuditFilters {
  dateFrom: string;
  dateTo: string;
  /** Staff ids, comma-separated (staffFilterParam); '' means all staff. */
  userId: string;
  /** Digits only; '' means no number search. */
  record: string;
}

/** What the download is started with — the filters on screen, nothing more. */
export function buildExportFilters(f: UserAuditFilters): Record<string, string> {
  const out: Record<string, string> = { dateFrom: f.dateFrom, dateTo: f.dateTo };
  if (f.userId && f.userId !== 'all') out.userId = f.userId;
  const record = digitsOnly(f.record);
  if (record) out.record = record;
  return out;
}

/** The list's query string: the export filters plus the page. */
export function buildListParams(
  f: UserAuditFilters,
  page: number,
  perPage = PER_PAGE,
): Record<string, string | number> {
  return { ...buildExportFilters(f), page, perPage };
}

interface Stamp {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
}

/** "YYYY-MM-DD HH:MM:SS" read as written — it is already the Account's local time. */
function parseStamp(value: string): Stamp | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]), h: Number(m[4]), mi: Number(m[5]) };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dateText(s: Stamp): string {
  return `${MONTHS[s.mo - 1]} ${s.d}, ${s.y}`;
}

function clockText(s: Stamp): string {
  const h12 = s.h % 12 === 0 ? 12 : s.h % 12;
  return `${h12}:${String(s.mi).padStart(2, '0')} ${s.h < 12 ? 'AM' : 'PM'}`;
}

/**
 * A row's time, formatted by hand rather than through `new Date`: the server
 * has already converted it to the Account's timezone, and letting the browser
 * parse it would shift it into the viewer's own.
 */
export function formatAuditTime(occurredAt: string): string {
  const s = parseStamp(occurredAt);
  return s ? `${dateText(s)}, ${clockText(s)}` : occurredAt;
}

/**
 * R13 — the line saying the list cannot reach back past the day recording
 * began. Null when the chosen range starts on or after that moment.
 */
export function notRecordedNotice(recordingStartedAt: string | null, dateFrom: string): string | null {
  if (recordingStartedAt === null) return NOTHING_RECORDED_YET;
  const s = parseStamp(recordingStartedAt);
  if (!s) return null;
  if (recordingStartedAt.slice(0, 19) <= `${dateFrom} 00:00:00`) return null;
  return `Activity before ${dateText(s)} was not recorded.`;
}

/**
 * Every staff member, inactive ones included — someone who has left is exactly
 * who an audit is often about. Inactive staff are marked so the list reads.
 */
export function staffFilterOptions(users: AdminUser[]): FilterOption[] {
  return users
    .map((u) => {
      const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.userName || `Staff member ${u.userId}`;
      return { value: String(u.userId), label: u.active ? name : `${name} (inactive)` };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export interface LoadFailure {
  message: string;
  /** A refusal is a decision, not a fault: trying again changes nothing. */
  canRetry: boolean;
}

export function describeLoadFailure(error: unknown): LoadFailure {
  if (axios.isAxiosError(error) && error.response?.status === 422) {
    const body = error.response.data as { error?: { code?: string; message?: string } } | undefined;
    if (body?.error?.code === DENIED_CODE && body.error.message) {
      return { message: body.error.message, canRetry: false };
    }
  }
  return { message: LOAD_FAILED_MESSAGE, canRetry: true };
}
